import React, { useState, useRef, useEffect, useCallback } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { MobileResponsive } from "./MobileResponsive";
import Sidebar from "../components/Sidebar";
import useDataFetch from "../hooks/useDataFetch";
import { convertToLocalTime } from "../utils/convertTime";
import DynamicMetaTags from "../components/DynamicMetaTags";
import { useDispatch } from "react-redux";
import {
  cancelTripService,
  driverSearchService,
  assignDriverService,
} from "../features/outletSlice";
import toast from "react-hot-toast";
import {
  ArrowLeft,
  Clock,
  MapPin,
  DollarSign,
  User,
  Phone,
  MessageSquare,
  Truck,
  Tag,
  CreditCard,
  CheckCircle,
  AlertCircle,
  Package,
  Calendar,
  ChevronRight,
  ShoppingBag,
  Info,
  Receipt,
} from "lucide-react";
import { Map } from "@vis.gl/react-google-maps";
import { Marker } from "@vis.gl/react-google-maps";
import Directions from "../components/Directions";
import { collection, query, where, onSnapshot, doc } from "firebase/firestore";
import { db } from "../api/services/firebase";
 



// Status categories with clearer organization
const STATUS = {
  PENDING: [
    "Pending",
    "Cancelled",
    "Cancelled-By-Outlet",
    "Cancelled-By-Driver",
    "Cancelled-By-Admin",
    "Nobody-Accepted",
  ],
  DELIVERING: [
    "Driver-Assigned",
    "Driver-Approaching",
    "Driver-Arrived",
    "Picked-Up",
    "Driver-Departed",
  ],
  DONE: ["Delivered", "Completed"],
};

// Helper function to format date consistently
const formatDate = (dateString) => {
  return convertToLocalTime(dateString);
};

// Helper function to format currency consistently
const formatCurrency = (amount) => {
  return `£${(Number(amount) || 0).toFixed(2)}`;
};

// Helper function for getting status display name
const getStatusDisplayName = (status) => {
  return status?.replace(/-/g, " ") || "Unknown";
};

// Status badge component with consistent styling
const StatusBadge = ({ status }) => {
  let config = {
    bgColor: "bg-gray-100",
    textColor: "text-gray-700",
    icon: <Clock className="w-4 h-4 mr-1" />,
  };

  if (STATUS.DONE.includes(status)) {
    config = {
      bgColor: "bg-green-100",
      textColor: "text-green-700",
      icon: <CheckCircle className="w-4 h-4 mr-1" />,
    };
  } else if (STATUS.PENDING.includes(status)) {
    config = {
      bgColor: "bg-red-100",
      textColor: "text-red-700",
      icon: <AlertCircle className="w-4 h-4 mr-1" />,
    };
  } else if (STATUS.DELIVERING.includes(status)) {
    config = {
      bgColor: "bg-amber-100",
      textColor: "text-amber-700",
      icon: <Truck className="w-4 h-4 mr-1" />,
    };
  }

  return (
    <div
      className={`flex items-center px-3 py-1.5 rounded-full ${config.bgColor} ${config.textColor}`}
    >
      {config.icon}
      <span className="text-sm font-medium">
        {getStatusDisplayName(status)}
      </span>
    </div>
  );
};

// Info card component for consistent styling
const InfoCard = ({ children, className = "" }) => {
  return (
    <div
      className={`p-6 rounded-xl bg-gray-50 shadow-sm border border-gray-200 ${className}`}
    >
      {children}
    </div>
  );
};

// Section title component for consistent styling
const SectionTitle = ({ children }) => {
  return (
    <h3 className="font-medium text-gray-800 mb-4 flex items-center gap-2">
      {children}
    </h3>
  );
};

const getLatLng = (obj, latKey, lngKey) => {
  if (!obj) return null;
  const lat = parseFloat(obj[latKey]);
  const lng = parseFloat(obj[lngKey]);
  if (isNaN(lat) || isNaN(lng)) return null;
  return { lat, lng };
};

// Parse dest_coordinates string to { lat, lng }
const getLatLngFromString = (str) => {
  if (!str) return null;
  const [lat, lng] = str.split(",").map(Number);
  if (isNaN(lat) || isNaN(lng)) return null;
  return { lat, lng };
};

const LiveOrderMap = ({ order }) => {
  const [driverLocation, setDriverLocation] = useState(null);
  const [loading, setLoading] = useState(true);
  const [mapCenter, setMapCenter] = useState(null);
  console.log(order);

  // Get outlet and customer locations
  const outletLocation = getLatLng(order.outletDetails, "coordinatesX", "coordinatesY");
  console.log('customer_coordinates',order.destination_coordinates);
  const customerLocation = getLatLngFromString(order.destination_coordinates);
  console.log('customerLocation',customerLocation);

  // Listen to driver's live location from Firestore
  useEffect(() => {
    if (!order.driver?.id) return;
    setLoading(true);
    const unsub = onSnapshot(doc(db, process.env.REACT_APP_COLLECTION_DRIVER, order.driver.id), (docSnap) => {
      if (docSnap.exists()) {
        const { coordinatesX, coordinatesY } = docSnap.data();
        if (coordinatesX && coordinatesY) {
          setDriverLocation({ lat: parseFloat(coordinatesX), lng: parseFloat(coordinatesY) });
        }
      }
      setLoading(false);
    });
    return () => unsub();
  }, [order.driver?.id]);

  // Initialize map center only once when coordinates are available
  useEffect(() => {
    if (driverLocation && outletLocation && !mapCenter) {
      const center = {
        lat: (driverLocation.lat + outletLocation.lat) / 2,
        lng: (driverLocation.lng + outletLocation.lng) / 2
      };
      setMapCenter(center);
    }
  }, [driverLocation, outletLocation, mapCenter]);

  let destination = null;
  if (driverLocation) {
    if (["Driver-Assigned", "Driver-Approaching", "Driver-Arrived"].includes(order.status)) {
      destination = outletLocation;
      console.log('outletLocation',destination);
    } else if (["Picked-Up", "Driver-Departed"].includes(order.status)) {
      destination = customerLocation;
      console.log('customerLocation',destination);
    }
  }

  if (!driverLocation || !destination) return null;

  return (
    <div className="w-full h-96 my-6 border-2 border-quaternary rounded-lg overflow-hidden">
      <Map
        mapId={"terrain"}
        defaultZoom={12}
        defaultCenter={mapCenter || driverLocation}
        gestureHandling={"greedy"}
        fullscreenControl={true}
        style={{ width: "100%", height: "100%" }}
      >
        <Directions origin={driverLocation} destination={destination} />
      </Map>
    </div>
  );
};

// OrderDetails component
const OrderDetails = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { data, isLoading, mutate } = useDataFetch(`/admin/order-detail/${id}`);
  const [activeTab, setActiveTab] = useState("summary");
  const [cancelConfirm, setCancelConfirm] = useState(false);
  const [cancelling, setCancelling] = useState(false);
  const [reassignOpen, setReassignOpen] = useState(false);
  const [availableDrivers, setAvailableDrivers] = useState([]);
  const [searchLoading, setSearchLoading] = useState(false);
  const [assigning, setAssigning] = useState(null); // holds driverId being assigned

  const handleCancelOrder = async () => {
    const order = data?.orderDTO;
    setCancelling(true);
    const resultAction = await dispatch(
      cancelTripService({ orderId: id, driverId: order?.driver?.id })
    );
    setCancelling(false);
    setCancelConfirm(false);
    if (cancelTripService.fulfilled.match(resultAction)) {
      toast.success(resultAction?.payload?.message || "Order cancelled successfully");
      mutate();
    }
  };

  const handleSearchDrivers = async () => {
    const order = data?.orderDTO;
    const rejectedDrivers = order?.order_rejected_by_drivers || [];
    const prevdriverId = rejectedDrivers[rejectedDrivers.length - 1] || order?.driver?.id;
    setSearchLoading(true);
    setAvailableDrivers([]);
    const resultAction = await dispatch(
      driverSearchService({ orderId: order?.orderId, prevdriverId })
    );
    setSearchLoading(false);
    if (driverSearchService.fulfilled.match(resultAction)) {
      setAvailableDrivers(resultAction.payload?.drivers || []);
      if (!resultAction.payload?.drivers?.length) {
        toast.error("No available drivers found nearby.");
      }
    }
  };

  const handleAssignDriver = async (driverId) => {
    const order = data?.orderDTO;
    const rejectedDrivers = order?.order_rejected_by_drivers || [];
    const prevdriverId = rejectedDrivers[rejectedDrivers.length - 1] || order?.driver?.id;
    setAssigning(driverId);
    const resultAction = await dispatch(
      assignDriverService({ orderId: order?.orderId, driverId, prevdriverId })
    );
    setAssigning(null);
    if (assignDriverService.fulfilled.match(resultAction)) {
      toast.success(resultAction?.payload?.message || "Driver assigned successfully");
      setReassignOpen(false);
      setAvailableDrivers([]);
      mutate();
    }
  };

  if (isLoading) {
    return (
      <>
        <MobileResponsive />
        <div className="lg:flex hidden items-start">
          <Sidebar />
          <div className="ml-[14.5vw] w-full h-screen flex items-center justify-center bg-gray-50">
            <div className="flex flex-col items-center gap-2">
              <div className="h-12 w-12 rounded-full border-4 border-t-quaternary border-b-quaternary border-r-transparent border-l-transparent animate-spin"></div>
              <p className="text-gray-600 font-medium">Loading..</p>
            </div>
          </div>
        </div>
      </>
    );
  }

  const order = data?.orderDTO;
  if (!order) {
    return (
      <>
        <MobileResponsive />
        <div className="lg:flex hidden items-start">
          <Sidebar />
          <div className="ml-[14.5vw] w-full h-screen flex items-center justify-center">
            <div className="text-center p-6 bg-gray-50 rounded-xl shadow-sm max-w-md">
              <AlertCircle className="w-12 h-12 text-red-500 mx-auto mb-4" />
              <h2 className="text-xl font-bold text-gray-800 mb-2">
                Order Not Found
              </h2>
              <p className="text-gray-600 mb-4">
                We couldn't find details for this order.
              </p>
              <button
                onClick={() => navigate(-1)}
                className="px-4 py-2 bg-quaternary text-white rounded-lg hover:bg-orange-600 transition-colors"
              >
                Go Back
              </button>
            </div>
          </div>
        </div>
      </>
    );
  }

  // Calculate values safely with fallbacks
  const orderItems = order.delivery_items || [];
  const totalOrderValue =
    (Number(order.food_order_value) || 0) + (Number(order.delivery_price) || 0);

  return (
    <>
      <DynamicMetaTags
        pageTitle="Beep - Order Details & Payment Analytics"
        pageDescription="Access comprehensive order details including payment information, delivery tracking, and financial analytics with Beep's powerful order management system."
        pageKeywords="Beep, order details, payment history, order tracking, financial analytics, delivery management"
      />
      <MobileResponsive />
      <div className="lg:flex hidden items-start bg-white">
        <Sidebar />

        <div className="ml-[14.5vw] w-full h-screen overflow-y-auto px-8 py-8">
          {/* Header with back button */}
          <div className="flex items-center justify-between mb-6">
            <button
              className="flex items-center gap-3"
              onClick={() => navigate(-1)}
            >
              <div className="bg-quaternary w-5 h-5 flex items-center justify-center rounded-full pr-[1.5px]">
                <img
                  src="/assets/left-white-arrow.svg"
                  alt=""
                  className="w-[6px]"
                />
              </div>
              <span className="font-semibold text-base text-gray-800">
                Order details
              </span>
            </button>
          </div>

          {/* Order header */}
          <div className="bg-white rounded-xl shadow-sm p-6 mb-6 border border-gray-200">
            <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
              <div className="flex items-center gap-4">
                <div className="w-12 h-12 bg-orange-100 rounded-xl flex items-center justify-center text-quaternary">
                  <ShoppingBag className="w-6 h-6" />
                </div>
                <div>
                  <h1 className="text-2xl font-bold text-gray-800">
                    #{order.custom_orderId || id}
                  </h1>
                  <div className="flex items-center gap-2 text-sm text-gray-500">
                    <Calendar className="w-4 h-4" />
                    <span>{formatDate(order.createdAt)}</span>
                  </div>
                </div>
              </div>
              <div className="flex flex-col items-end gap-3">
                <StatusBadge status={order.status} />
                {["Cancelled", "Cancelled-By-Outlet", "Cancelled-By-Driver", "Cancelled-By-Admin"].includes(order.status) && (
                  <button
                    onClick={() => { setReassignOpen(true); setAvailableDrivers([]); }}
                    className="px-4 py-1.5 bg-quaternary text-white text-sm font-semibold rounded-lg hover:bg-orange-600 transition-colors"
                  >
                    Reassign Driver
                  </button>
                )}
                {STATUS.DELIVERING.includes(order.status) && order.driver?.id && (
                  <div className="flex items-center gap-2">
                    {cancelConfirm ? (
                      <>
                        <span className="text-sm text-gray-500">Confirm cancel?</span>
                        <button
                          onClick={handleCancelOrder}
                          disabled={cancelling}
                          className="px-4 py-1.5 bg-red-500 text-white text-sm font-semibold rounded-lg hover:bg-red-600 transition-colors disabled:opacity-60"
                        >
                          {cancelling ? "Cancelling…" : "Yes, cancel"}
                        </button>
                        <button
                          onClick={() => setCancelConfirm(false)}
                          disabled={cancelling}
                          className="px-4 py-1.5 border border-gray-300 text-gray-600 text-sm font-semibold rounded-lg hover:bg-gray-50 transition-colors"
                        >
                          Go back
                        </button>
                      </>
                    ) : (
                      <button
                        onClick={() => setCancelConfirm(true)}
                        className="px-4 py-1.5 bg-red-500 text-white text-sm font-semibold rounded-lg hover:bg-red-600 transition-colors"
                      >
                        Cancel Order
                      </button>
                    )}
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Tabs */}
          <div className="bg-white rounded-xl shadow-md mb-6 p-1 flex border border-gray-200 relative">
            {/* Active Tab Indicator */}
            <div
              className="absolute rounded-xl bg-quaternary transition-all duration-300 ease-in-out"
              style={{
                width: `${
                  activeTab === "summary"
                    ? `${100 / 2.96}%`
                    : activeTab === "items"
                    ? `${100 / 3.04}%`
                    : `${100 / 3}%`
                } `,
                left: `${
                  (["summary", "items", "payment"].indexOf(activeTab) * 100) / 3
                }%`,
                top: "4px",
                bottom: "4px",
                zIndex: 0,
              }}
            />

            {/* Tabs */}
            {["summary", "items", "payment"].map((tab) => (
              <button
                key={tab}
                className={`relative z-10 flex-1 py-2 px-2 rounded-lg font-medium transition-colors duration-300 flex items-center justify-center ${
                  activeTab === tab
                    ? "text-white"
                    : "text-gray-600 hover:text-gray-800"
                }`}
                onClick={() => setActiveTab(tab)}
              >
                <span className="transform transition-transform duration-200 ease-out scale-100 hover:scale-105 text-sm">
                  {tab.charAt(0).toUpperCase() + tab.slice(1)}
                </span>
              </button>
            ))}
          </div>

          {/* Main content */}
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 pb-8">
            {/* Left column - Order content */}
            <div className="lg:col-span-2 space-y-6">
              {/* Tab content */}
              {activeTab === "summary" && (
                <>
                  {/* Restaurant info */}
                  <InfoCard>
                    <div className="flex items-center gap-4">
                      <div className="w-16 h-16 rounded-xl bg-gray-200 flex items-center justify-center overflow-hidden">
                        {order.outletDetails?.image ? (
                          <img
                            src={order.outletDetails.image}
                            alt="Restaurant"
                            className="w-10 h-10 object-contain"
                          />
                        ) : (
                          <Package className="w-8 h-8 text-gray-400" />
                        )}
                      </div>
                      <div>
                        <h3 className="font-semibold text-lg text-gray-800 capitalize">
                          {order.outletDetails?.name ||
                            "Restaurant information not available"}
                        </h3>
                        <div className="flex items-center text-gray-500 gap-2 mt-1">
                          <Phone className="w-4 h-4" />
                          <span>{order.outletDetails?.phone || "N/A"}</span>
                        </div>
                      </div>
                    </div>
                  </InfoCard>

                  {/* Timeline */}
                  <InfoCard>
                    <SectionTitle>
                      <Clock className="w-5 h-5 text-quaternary" />
                      Order Timeline
                    </SectionTitle>
                    <div className="space-y-8">
                      {/* Order Placed */}
                      <div className="flex items-start">
                        <div className="flex flex-col items-center">
                          <div className="w-10 h-10 rounded-full bg-quaternary text-white flex items-center justify-center">
                            <CheckCircle className="w-5 h-5" />
                          </div>
                          <div className="w-0.5 h-16 bg-gray-200 mt-2"></div>
                        </div>
                        <div className="ml-4">
                          <p className="font-semibold text-gray-800">
                            Order Placed
                          </p>
                          <p className="text-gray-500 mt-1">
                            {formatDate(order.createdAt)}
                          </p>
                        </div>
                      </div>

                      {/* Processing */}
                      <div className="flex items-start">
                        <div className="flex flex-col items-center">
                          <div
                            className={`w-10 h-10 rounded-full text-white flex items-center justify-center ${
                              STATUS.DELIVERING.includes(order.status)
                                ? "bg-amber-500"
                                : STATUS.DONE.includes(order.status)
                                ? "bg-amber-500"
                                : "bg-gray-300"
                            }`}
                          >
                            <Truck className="w-5 h-5" />
                          </div>
                          <div className="w-0.5 h-16 bg-gray-200 mt-2"></div>
                        </div>
                        <div className="ml-4">
                          <p className="font-semibold text-gray-800">
                            Order Processing
                          </p>
                          <p className="text-gray-500 mt-1">
                            {STATUS.DELIVERING.includes(order.status)
                              ? "In progress"
                              : STATUS.DONE.includes(order.status)
                              ? "Completed"
                              : STATUS.PENDING.includes(order.status) &&
                                order.status === "Nobody-Accepted"
                              ? "Waiting for driver"
                              : "Pending"}
                          </p>
                        </div>
                      </div>

                      {/* Completion */}
                      <div className="flex items-start">
                        <div className="flex flex-col items-center">
                          <div
                            className={`w-10 h-10 rounded-full text-white flex items-center justify-center ${
                              STATUS.DONE.includes(order.status)
                                ? "bg-green"
                                : "bg-gray-300"
                            }`}
                          >
                            <CheckCircle className="w-5 h-5" />
                          </div>
                        </div>
                        <div className="ml-4">
                          <p className="font-semibold text-gray-800">
                            Order Completed
                          </p>
                          <p className="text-gray-500 mt-1">
                            {STATUS.DONE.includes(order.status)
                              ? formatDate(
                                  order.delivery_time || order.updatedAt
                                )
                              : "Pending"}
                          </p>
                        </div>
                      </div>
                    </div>
                  </InfoCard>

                  {/* Delivery info */}
                  <InfoCard>
                    <SectionTitle>
                      <MapPin className="w-5 h-5 text-quaternary" />
                      Delivery Information
                    </SectionTitle>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                      <div className="flex flex-col space-y-4 h-full">
                        <div className="p-4 bg-white rounded-lg flex-grow border border-gray-100">
                          <p className="text-sm font-medium text-gray-600 mb-1">
                            Customer
                          </p>
                          <p className="text-gray-800 font-medium capitalize">
                            {order.customer_name}
                          </p>
                          <div className="flex items-center gap-2 mt-2 text-gray-500">
                            <Phone className="w-4 h-4" />
                            <span>{order.customer_phone}</span>
                          </div>
                        </div>

                        {order.delivery_instructions && (
                          <div className="p-4 bg-white rounded-lg flex-grow border border-gray-100">
                            <p className="text-sm font-medium text-gray-600 mb-1">
                              Instructions
                            </p>
                            <p className="text-gray-800">
                              {order.delivery_instructions}
                            </p>
                          </div>
                        )}
                      </div>

                      <div className="flex flex-col space-y-4 h-full">
                        <div className="p-4 bg-white rounded-lg flex-grow border border-gray-100">
                          <p className="text-sm font-medium text-gray-600 mb-1">
                            Delivery Address
                          </p>
                          <p className="text-gray-800">{order.address}</p>
                          <p className="text-gray-500 text-sm mt-1">
                            Zip code:{" "}
                            {order.zipcode !== "N/A"
                              ? order.zipcode
                              : "Not provided"}
                          </p>
                        </div>
                      </div>
                    </div>
                  </InfoCard>

                  {/* Driver info */}
                  <InfoCard>
                    <SectionTitle>
                      <User className="w-5 h-5 text-quaternary" />
                      Driver Information
                    </SectionTitle>
                    {order.driver && order.driver.id ? (
                      <div className="flex items-center gap-5">
                        <div className="w-16 h-16 rounded-full bg-gray-100 flex items-center justify-center overflow-hidden border border-gray-200">
                          {order.driver.image ? (
                            <img
                              src={order.driver.image}
                              alt="Driver"
                              className="w-full h-full rounded-full object-cover"
                            />
                          ) : (
                            <User className="w-8 h-8 text-gray-400" />
                          )}
                        </div>
                        <div>
                          <h4 className="font-semibold text-lg text-gray-800 capitalize">
                            {order.driver.name}
                          </h4>
                          <div className="flex items-center gap-2 text-gray-500 mt-1">
                            <Phone className="w-4 h-4" />
                            <span>{order.driver.phone}</span>
                          </div>
                        </div>
                      </div>
                    ) : (
                      <div className="bg-white p-6 rounded-lg text-center border border-gray-100">
                        <User className="w-12 h-12 text-gray-300 mx-auto mb-3" />
                        <p className="text-gray-600 font-medium">
                          No driver assigned yet
                        </p>
                        <p className="text-gray-500 text-sm mt-1">
                          A driver will be assigned to this order soon
                        </p>
                      </div>
                    )}
                  </InfoCard>
                </>
              )}

              {activeTab === "items" && (
                <InfoCard>
                  <SectionTitle>
                    <Package className="w-5 h-5 text-quaternary" />
                    Order Items
                  </SectionTitle>
                  {orderItems.length > 0 ? (
                    <>
                      <div className="overflow-hidden rounded-lg border border-gray-200">
                        <table className="min-w-full divide-y divide-gray-200">
                          <thead className="bg-gray-100">
                            <tr>
                              <th
                                scope="col"
                                className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
                              >
                                Item
                              </th>
                              <th
                                scope="col"
                                className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
                              >
                                Quantity
                              </th>
                              <th
                                scope="col"
                                className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider"
                              >
                                Price
                              </th>
                              <th
                                scope="col"
                                className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider"
                              >
                                Total
                              </th>
                            </tr>
                          </thead>
                          <tbody className="bg-white divide-y divide-gray-200">
                            {orderItems.map((item, index) => {
                              const itemPrice = Number(item.price) || 0;
                              const itemQuantity = Number(item.quantity) || 0;
                              const itemTotal = itemPrice * itemQuantity;
                              const modifiers = item.options_to_add || [];

                              return (
                                <React.Fragment key={index}>
                                  <tr>
                                    <td className="px-6 py-4 whitespace-nowrap">
                                      <div className="flex items-center">
                                        <div className="flex-shrink-0 h-10 w-10 bg-orange-100 rounded-lg flex items-center justify-center">
                                          <Tag className="h-5 w-5 text-quaternary" />
                                        </div>
                                        <div className="ml-4">
                                          <div className="text-sm font-medium text-gray-800">
                                            {item.item}
                                          </div>
                                        </div>
                                      </div>
                                    </td>
                                    <td className="px-6 py-4 whitespace-nowrap">
                                      <div className="text-sm text-gray-800">
                                        {itemQuantity}
                                      </div>
                                    </td>
                                    <td className="px-6 py-4 whitespace-nowrap text-right">
                                      <div className="text-sm text-gray-800">
                                        {formatCurrency(itemPrice)}
                                      </div>
                                    </td>
                                    <td className="px-6 py-4 whitespace-nowrap text-right">
                                      <div className="text-sm font-medium text-gray-800">
                                        {formatCurrency(itemTotal)}
                                      </div>
                                    </td>
                                  </tr>
                                  {modifiers.length > 0 && (
                                    <tr>
                                      <td colSpan="4" className="px-6 pb-4 pt-0">
                                        <div className="ml-14 flex flex-wrap gap-2">
                                          {modifiers.map((mod, modIndex) => (
                                            <span
                                              key={mod._id || modIndex}
                                              className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full bg-orange-50 border border-orange-100 text-xs text-gray-700"
                                            >
                                              <span className="font-medium text-gray-500">{mod.group}:</span>
                                              <span>{mod.title}</span>
                                              {Number(mod.price) > 0 && (
                                                <span className="text-quaternary font-medium">
                                                  +{formatCurrency(mod.price)}
                                                </span>
                                              )}
                                            </span>
                                          ))}
                                        </div>
                                      </td>
                                    </tr>
                                  )}
                                </React.Fragment>
                              );
                            })}
                          </tbody>
                          <tfoot className="bg-gray-50">
                            <tr>
                              <td
                                colSpan="3"
                                className="px-6 py-4 text-right font-medium text-gray-700"
                              >
                                Order Subtotal
                              </td>
                              <td className="px-6 py-4 text-right font-medium text-gray-800">
                                {formatCurrency(order.food_order_value)}
                              </td>
                            </tr>
                            <tr>
                              <td
                                colSpan="3"
                                className="px-6 py-4 text-right font-medium text-gray-700"
                              >
                                Delivery Fee
                              </td>
                              <td className="px-6 py-4 text-right font-medium text-gray-800">
                                {formatCurrency(order.delivery_price)}
                              </td>
                            </tr>
                            <tr className="border-t-2 border-gray-200">
                              <td
                                colSpan="3"
                                className="px-6 py-4 text-right font-semibold text-gray-900"
                              >
                                Total
                              </td>
                              <td className="px-6 py-4 text-right font-semibold text-gray-900">
                                {formatCurrency(totalOrderValue)}
                              </td>
                            </tr>
                          </tfoot>
                        </table>
                      </div>

                      <div className="mt-6 bg-orange-50 border border-orange-100 rounded-lg p-4 flex items-start">
                        <Info className="w-5 h-5 text-quaternary mt-0.5 mr-3 flex-shrink-0" />
                        <div>
                          <p className="text-sm text-gray-700">
                            Payment was processed via{" "}
                            <span className="font-medium">
                              {order.mode_of_payment || "N/A"}
                            </span>
                            . The total includes a delivery fee of{" "}
                            {formatCurrency(order.delivery_price)}.
                          </p>
                        </div>
                      </div>
                    </>
                  ) : (
                    <div className="bg-white p-8 rounded-lg text-center border border-gray-100">
                      <Package className="w-12 h-12 text-gray-300 mx-auto mb-3" />
                      <p className="text-gray-700 font-medium">
                        No items available
                      </p>
                      <p className="text-gray-500 text-sm mt-1">
                        This order doesn't have any items listed
                      </p>
                    </div>
                  )}
                </InfoCard>
              )}

              {activeTab === "payment" && (
                <InfoCard>
                  <SectionTitle>
                    <Receipt className="w-5 h-5 text-quaternary" />
                    Payment Details
                  </SectionTitle>

                  <div className="space-y-6">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                      <div className="p-5 rounded-lg bg-white border border-gray-100">
                        <div className="flex items-center gap-3 mb-4">
                          <div className="w-10 h-10 bg-orange-100 rounded-full flex items-center justify-center">
                            <CreditCard className="w-5 h-5 text-quaternary" />
                          </div>
                          <h4 className="font-semibold text-gray-800">
                            Payment Method
                          </h4>
                        </div>
                        <div className="text-xl font-medium text-gray-700">
                          {order.mode_of_payment || "Not specified"}
                        </div>
                      </div>

                      <div className="p-5 rounded-lg bg-white border border-gray-100">
                        <div className="flex items-center gap-3 mb-4">
                          <div className="w-10 h-10 bg-orange-100 rounded-full flex items-center justify-center">
                            <Calendar className="w-5 h-5 text-quaternary" />
                          </div>
                          <h4 className="font-semibold text-gray-800">
                            Payment Date
                          </h4>
                        </div>
                        <div className="text-lg font-medium text-gray-700">
                          {formatDate(order.createdAt)}
                        </div>
                      </div>
                    </div>

                    <div className="overflow-hidden rounded-lg border border-gray-200">
                      <div className="bg-gray-100 px-6 py-3 border-b border-gray-200">
                        <h3 className="font-semibold text-gray-800">
                          Payment Breakdown
                        </h3>
                      </div>
                      <div className="px-6 py-4 bg-white">
                        <div className="space-y-3">
                          <div className="flex justify-between py-2">
                            <span className="text-gray-600">Order Value</span>
                            <span className="font-medium text-gray-800">
                              {formatCurrency(order.food_order_value)}
                            </span>
                          </div>
                          <div className="flex justify-between py-2">
                            <span className="text-gray-600">Delivery Fee</span>
                            <span className="font-medium text-gray-800">
                              {formatCurrency(order.delivery_price)}
                            </span>
                          </div>
                          <div className="border-t border-gray-100 my-2"></div>
                          <div className="flex justify-between py-2">
                            <span className="text-gray-800 font-medium">
                              Total Amount
                            </span>
                            <span className="font-bold text-gray-900">
                              {formatCurrency(totalOrderValue)}
                            </span>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </InfoCard>
              )}
            </div>

            {/* Right column - Finance summary */}
            <div className="lg:col-span-1 space-y-6">
              <div className="flex flex-col gap-4">
                  <LiveOrderMap order={order} />
              </div>
              

              <InfoCard className="bg-gradient-to-br from-quaternary to-orange-600 text-white">
                <h3 className="font-semibold text-lg mb-4 flex items-center gap-2">
                  <DollarSign className="w-5 h-5" />
                  Financial Overview
                </h3>
                <div className="space-y-6">
                  <div className="bg-white/10 rounded-lg p-4 backdrop-blur-sm">
                    <div className="text-sm text-white/80 mb-1">
                      Order Total
                    </div>
                    <div className="text-3xl font-bold">
                      {formatCurrency(totalOrderValue)}
                    </div>
                  </div>

                  <div className="grid grid-cols-2 gap-4">
                    <div className="bg-white/10 rounded-lg p-4 backdrop-blur-sm">
                      <div className="text-sm text-white/80 mb-1">
                        Order Value
                      </div>
                      <div className="text-lg font-medium">
                        {formatCurrency(order.food_order_value)}
                      </div>
                    </div>
                    <div className="bg-white/10 rounded-lg p-4 backdrop-blur-sm">
                      <div className="text-sm text-white/80 mb-1">
                        Delivery Fee
                      </div>
                      <div className="text-lg font-medium">
                        {formatCurrency(order.delivery_price)}
                      </div>
                    </div>
                  </div>

                  <div className="bg-white/10 rounded-lg p-4 backdrop-blur-sm">
                    <div className="text-sm text-white/80 mb-1">
                      Beep Commission
                    </div>
                    <div className="text-3xl font-bold">
                      {formatCurrency(order.commission_amount)}
                    </div>
                  </div>
                </div>
              </InfoCard>
            </div>
          </div>
        </div>
      </div>

      {/* Reassign Driver Sidebar */}
      {reassignOpen && (
        <div className="fixed inset-0 bg-black bg-opacity-50 backdrop-blur-sm z-30" onClick={() => setReassignOpen(false)} />
      )}
      <div
        className={`fixed right-0 top-0 h-full w-[420px] bg-white shadow-xl overflow-y-auto transform transition-transform duration-500 ease-in-out z-40 border-t-4 border-quaternary ${
          reassignOpen ? "translate-x-0" : "translate-x-full"
        }`}
      >
        <div className="p-8 flex flex-col gap-6">
          {/* Header */}
          <div className="flex items-center justify-between">
            <h2 className="text-lg font-bold text-gray-800">Reassign Driver</h2>
            <button
              onClick={() => setReassignOpen(false)}
              className="text-gray-400 hover:text-gray-600 text-2xl leading-none"
            >
              &times;
            </button>
          </div>

          {/* Order summary */}
          <div className="bg-gray-50 rounded-lg p-4 border border-gray-200 text-sm">
            <p className="font-semibold text-gray-700">Order #{order?.custom_orderId}</p>
            <p className="text-gray-500 mt-1 capitalize">{order?.customer_name}</p>
            <p className="text-gray-500 mt-0.5 truncate">{order?.address}</p>
          </div>

          {/* Search button */}
          <button
            onClick={handleSearchDrivers}
            disabled={searchLoading}
            className="w-full bg-quaternary text-white font-semibold py-2.5 rounded-lg hover:bg-orange-600 transition-colors disabled:opacity-60 flex items-center justify-center gap-2"
          >
            {searchLoading ? (
              <>
                <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                Searching…
              </>
            ) : (
              "Search New Driver"
            )}
          </button>

          {/* Driver results */}
          {availableDrivers.length > 0 && (
            <div className="flex flex-col gap-3">
              <p className="text-sm font-semibold text-gray-600">
                {availableDrivers.length} driver{availableDrivers.length !== 1 ? "s" : ""} found
              </p>
              {availableDrivers.map((driver) => (
                <div
                  key={driver._id}
                  className="flex items-center justify-between p-4 bg-gray-50 border border-gray-200 rounded-lg gap-3"
                >
                  <div className="flex items-center gap-3 min-w-0">
                    {driver.driver_image ? (
                      <img
                        src={driver.driver_image}
                        alt={driver.name}
                        className="w-11 h-11 rounded-full object-cover flex-shrink-0 border border-gray-200"
                      />
                    ) : (
                      <div className="w-11 h-11 rounded-full bg-orange-100 flex items-center justify-center flex-shrink-0">
                        <User className="w-5 h-5 text-quaternary" />
                      </div>
                    )}
                    <div className="min-w-0">
                      <p className="font-semibold text-gray-800 capitalize truncate">{driver.name}</p>
                      <p className="text-sm text-gray-500">{driver.phone}</p>
                      <p className="text-xs text-gray-400 mt-0.5">
                        {driver.current_deliveries_count ?? 0} active deliver{driver.current_deliveries_count === 1 ? "y" : "ies"}
                      </p>
                    </div>
                  </div>
                  <button
                    onClick={() => handleAssignDriver(driver._id)}
                    disabled={assigning !== null}
                    className="px-4 py-1.5 bg-quaternary text-white text-sm font-semibold rounded-lg hover:bg-orange-600 transition-colors disabled:opacity-60 min-w-[80px] flex items-center justify-center flex-shrink-0"
                  >
                    {assigning === driver._id ? (
                      <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                    ) : (
                      "Assign"
                    )}
                  </button>
                </div>
              ))}
            </div>
          )}

          {!searchLoading && availableDrivers.length === 0 && (
            <p className="text-sm text-gray-400 text-center">
              Click "Search New Driver" to find available drivers near this order.
            </p>
          )}
        </div>
      </div>
    </>
  );
};

export default OrderDetails;
